<template>
  <div class="app-container">
    <div class="content-wrapper">
      <header class="nav-header">
        <div class="nav-title-row">
          <h1 class="nav-large-title">GPT正规订阅平台</h1>
          <button v-if="!isQueryMode" @click="switchToQuery" class="btn-tinted">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <span>查询订单/调换卡密</span>
          </button>
        </div>
        <p class="nav-subtitle">三步完成会员充值兑换</p>
      </header>

      <div v-if="!isQueryMode" class="recharge-tabs" role="tablist">
        <button
          type="button"
          :class="{ active: rechargeMode === 'single' }"
          @click="switchRechargeMode('single')"
        >
          单次充值
        </button>
        <button
          type="button"
          :class="{ active: rechargeMode === 'batch' }"
          @click="switchRechargeMode('batch')"
        >
          批量充值
        </button>
        <button
          type="button"
          :class="{ active: rechargeMode === 'account' }"
          @click="switchRechargeMode('account')"
        >
          账密充值
        </button>
      </div>

      <ProgressBar v-if="!isQueryMode && rechargeMode === 'single'" :currentStep="currentStep" />

      <div class="steps-container">
        <OrderQuery
          v-if="isQueryMode"
          @back="switchToExchange"
        />

        <template v-else>
          <BatchRecharge v-show="rechargeMode === 'batch'" />
          <AccountRecharge v-show="rechargeMode === 'account'" />

          <div v-show="rechargeMode === 'single'">
            <transition name="slide-fade" mode="out-in">
              <StepCard
                v-if="currentStep === 0"
                @validated="handleCardValidated"
              />
              <StepToken
                v-else-if="currentStep === 1"
                @validated="handleTokenValidated"
              />
              <StepOrder
                v-else-if="currentStep === 2"
                :cardCode="cardData.cardCode"
                :token="tokenData.token"
                :cardInfo="cardData.cardInfo"
                :tokenInfo="tokenData.tokenInfo"
                @completed="handleOrderCompleted"
                @reset="resetAll"
                @prev="prevStep"
              />
            </transition>
          </div>
        </template>
      </div>

      <div v-if="!isQueryMode && rechargeMode === 'single' && currentStep > 0 && currentStep <= 2 && !orderCompleted" class="nav-buttons">
        <button @click="prevStep" class="btn-plain">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          <span>上一步</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ProgressBar from './components/ProgressBar'
import StepCard from './components/StepCard'
import StepToken from './components/StepToken'
import StepOrder from './components/StepOrder'
import OrderQuery from './components/OrderQuery'
import BatchRecharge from './components/BatchRecharge'
import AccountRecharge from './components/AccountRecharge'

export default {
  name: 'App',
  components: {
    ProgressBar,
    StepCard,
    StepToken,
    StepOrder,
    OrderQuery,
    BatchRecharge,
    AccountRecharge
  },
  data() {
    return {
      currentStep: 0,
      isQueryMode: false,
      rechargeMode: 'single',
      cardData: {
        cardCode: '',
        cardInfo: null
      },
      tokenData: {
        token: '',
        tokenInfo: null
      },
      orderData: null,
      orderCompleted: false
    }
  },
  methods: {
    handleCardValidated(data) {
      this.cardData = {
        cardCode: data.cardCode,
        cardInfo: data.cardInfo
      }
      this.currentStep = 1
    },
    handleTokenValidated(data) {
      this.tokenData = {
        token: data.token,
        tokenInfo: data.tokenInfo
      }
      this.currentStep = 2
    },
    handleOrderCompleted(data) {
      this.orderData = data
      this.orderCompleted = true
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    resetAll() {
      this.currentStep = 0
      this.cardData = { cardCode: '', cardInfo: null }
      this.tokenData = { token: '', tokenInfo: null }
      this.orderData = null
      this.orderCompleted = false
    },
    switchToQuery() {
      this.isQueryMode = true
    },
    switchToExchange() {
      this.isQueryMode = false
      this.currentStep = 0
    },
    switchRechargeMode(mode) {
      if (this.rechargeMode === mode) return

      this.rechargeMode = mode
    }
  }
}
</script>

<style>
/* ===== iOS Design Tokens ===== */
:root {
  --ios-bg: #f2f2f7;               /* systemGroupedBackground */
  --ios-card: #ffffff;             /* secondarySystemGroupedBackground */
  --ios-fill: rgba(118, 118, 128, 0.12);  /* systemFill (inputs) */
  --ios-separator: rgba(60, 60, 67, 0.12);
  --ios-label: #1c1c1e;            /* label */
  --ios-label-secondary: rgba(60, 60, 67, 0.6);
  --ios-label-tertiary: rgba(60, 60, 67, 0.3);
  --ios-blue: #007aff;
  --ios-green: #34c759;
  --ios-red: #ff3b30;
  --ios-orange: #ff9500;
  --ios-yellow: #ffcc00;
  --ios-purple: #af52de;
  --ios-gray: #8e8e93;
  --ios-radius-card: 16px;
  --ios-radius-control: 12px;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  -webkit-text-size-adjust: 100%;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'PingFang SC',
    'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
  background: var(--ios-bg);
  color: var(--ios-label);
  min-height: 100vh;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}

.app-container {
  min-height: 100vh;
  padding: env(safe-area-inset-top) 16px env(safe-area-inset-bottom);
}

.content-wrapper {
  max-width: 760px;
  margin: 0 auto;
  padding: 32px 0 56px;
}

/* ===== Navigation (large title) ===== */
.nav-header {
  padding: 12px 4px 4px;
}

.nav-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.nav-title-row .btn-tinted {
  flex-shrink: 0;
}

.nav-large-title {
  font-size: 40px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.nav-subtitle {
  margin-top: 6px;
  font-size: 17px;
  color: var(--ios-label-secondary);
}

.recharge-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
  margin-top: 18px;
  padding: 4px;
  border-radius: 12px;
  background: var(--ios-fill);
}

.recharge-tabs button {
  min-height: 38px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--ios-label-secondary);
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.recharge-tabs button.active {
  background: var(--ios-card);
  color: var(--ios-label);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* ===== Shared iOS components ===== */

/* Grouped card */
.ios-card {
  background: var(--ios-card);
  border-radius: var(--ios-radius-card);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

/* Grouped section header / footer */
.ios-group-header {
  padding: 22px 18px 8px;
  font-size: 14px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--ios-label-secondary);
}

.ios-group-footer {
  padding: 8px 18px 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--ios-label-secondary);
}

/* Inset grouped list */
.ios-list {
  overflow: hidden;
}

.ios-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-height: 50px;
  padding: 13px 18px;
  position: relative;
}

.ios-row + .ios-row::before {
  content: '';
  position: absolute;
  top: 0;
  left: 18px;
  right: 0;
  height: 1px;
  background: var(--ios-separator);
  transform: scaleY(0.5);
}

.ios-row-label {
  font-size: 16px;
  color: var(--ios-label);
  flex-shrink: 0;
}

.ios-row-value {
  font-size: 16px;
  color: var(--ios-label-secondary);
  text-align: right;
  word-break: break-all;
}

/* Inputs */
.ios-input,
.ios-textarea {
  width: 100%;
  padding: 15px 16px;
  border: 1px solid transparent;
  border-radius: var(--ios-radius-control);
  background: var(--ios-fill);
  font-size: 17px;
  color: var(--ios-label);
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;
  font-family: inherit;
  appearance: none;
}

.ios-textarea {
  resize: none;
  line-height: 1.5;
}

.ios-input::placeholder,
.ios-textarea::placeholder {
  color: var(--ios-label-tertiary);
}

.ios-input:focus,
.ios-textarea:focus {
  border-color: var(--ios-blue);
  background: var(--ios-card);
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.15);
}

.ios-input.has-error,
.ios-textarea.has-error {
  border-color: var(--ios-red);
  box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.12);
}

.ios-field-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--ios-label-secondary);
  margin-bottom: 8px;
}

/* Buttons */
.btn-filled,
.btn-gray,
.btn-tinted,
.btn-plain {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  transition: opacity 0.15s ease, transform 0.1s ease, background 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.btn-filled {
  width: 100%;
  min-height: 54px;
  padding: 0 20px;
  border-radius: 14px;
  background: var(--ios-blue);
  color: #fff;
  font-size: 18px;
}

.btn-filled.btn-destructive {
  background: var(--ios-red);
}

.btn-filled:active:not(:disabled) {
  opacity: 0.75;
}

.btn-filled:disabled {
  background: rgba(0, 122, 255, 0.35);
  cursor: not-allowed;
}

.btn-filled.btn-destructive:disabled {
  background: rgba(255, 59, 48, 0.35);
}

.btn-gray {
  width: 100%;
  min-height: 54px;
  padding: 0 20px;
  border-radius: 14px;
  background: var(--ios-fill);
  color: var(--ios-blue);
  font-size: 18px;
}

.btn-gray:active {
  opacity: 0.65;
}

.btn-tinted {
  padding: 10px 18px;
  border-radius: 100px;
  background: rgba(0, 122, 255, 0.12);
  color: var(--ios-blue);
  font-size: 15px;
}

.btn-tinted:active {
  background: rgba(0, 122, 255, 0.22);
}

.btn-tinted svg {
  width: 15px;
  height: 15px;
}

.btn-plain {
  background: none;
  color: var(--ios-blue);
  font-size: 17px;
  font-weight: 400;
  padding: 8px 12px;
}

.btn-plain:active {
  opacity: 0.4;
}

.btn-plain svg {
  width: 18px;
  height: 18px;
  margin-left: -6px;
}

/* iOS activity indicator */
.ios-spinner {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  background: conic-gradient(from 0deg, transparent 40deg, currentColor 320deg);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px));
  animation: ios-spin 0.8s linear infinite;
}

@keyframes ios-spin {
  to {
    transform: rotate(360deg);
  }
}

/* UIProgressView style progress bar */
.ios-progress-track {
  height: 5px;
  border-radius: 100px;
  background: rgba(120, 120, 128, 0.2);
  overflow: hidden;
}

.ios-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: var(--ios-blue);
  transition: width 0.25s ease;
}

/* Callouts (tinted info blocks) */
.ios-callout {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 15px 16px;
  border-radius: var(--ios-radius-control);
  font-size: 14px;
  line-height: 1.55;
}

.ios-callout svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.ios-callout.is-info {
  background: rgba(0, 122, 255, 0.08);
  color: #0859ad;
}

.ios-callout.is-info svg {
  color: var(--ios-blue);
}

.ios-callout.is-warning {
  background: rgba(255, 149, 0, 0.1);
  color: #8a5300;
}

.ios-callout.is-warning svg {
  color: var(--ios-orange);
}

.ios-callout.is-danger {
  background: rgba(255, 59, 48, 0.08);
  color: #c0271d;
}

.ios-callout.is-danger svg {
  color: var(--ios-red);
}

.ios-callout-title {
  font-weight: 600;
  margin-bottom: 3px;
  font-size: 15px;
}

/* Inline field error */
.ios-error-text {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 13px;
  color: var(--ios-red);
}

.ios-error-text svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* Status colors (iOS palette) */
.status-pending   { color: var(--ios-orange) !important; }
.status-processing{ color: var(--ios-blue) !important; }
.status-completed { color: var(--ios-green) !important; }
.status-error     { color: var(--ios-red) !important; }
.status-returned  { color: var(--ios-purple) !important; }
.status-retrying  { color: var(--ios-orange) !important; }
.status-canceled,
.status-unknown   { color: var(--ios-gray) !important; }

.status-available   { color: var(--ios-green) !important; }
.status-used        { color: var(--ios-orange) !important; }
.status-unavailable { color: var(--ios-red) !important; }

/* ===== Layout pieces ===== */
.steps-container {
  margin-top: 8px;
}

.nav-buttons {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* Page transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter {
  opacity: 0;
  transform: translateX(24px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}

@media (max-width: 600px) {
  .app-container {
    padding-left: 14px;
    padding-right: 14px;
  }

  .content-wrapper {
    padding: 8px 0 40px;
  }

  .nav-header {
    padding: 10px 2px 2px;
  }

  .nav-large-title {
    font-size: 26px;
  }

  .nav-subtitle {
    font-size: 14px;
  }

  .ios-group-header {
    padding: 18px 16px 7px;
    font-size: 13px;
  }

  .ios-group-footer {
    padding: 7px 16px 0;
    font-size: 13px;
  }

  .ios-row {
    min-height: 46px;
    padding: 12px 16px;
  }

  .ios-row + .ios-row::before {
    left: 16px;
  }

  .ios-row-label,
  .ios-row-value {
    font-size: 15px;
  }

  /* 16px 以上可避免 iOS Safari 聚焦输入框时自动缩放页面 */
  .ios-input,
  .ios-textarea {
    padding: 13px 14px;
    font-size: 16px;
  }

  .btn-filled,
  .btn-gray {
    min-height: 50px;
    font-size: 17px;
    border-radius: 13px;
  }

  .ios-callout {
    padding: 13px 14px;
    font-size: 13px;
  }

  .ios-callout-title {
    font-size: 14px;
  }
}
</style>
